import React, { useState, useRef, useEffect } from 'react';
import styles from 'components/show_drawing/measure_tool.module.scss';

// Phase A: an ephemeral on-drawing measure tool for entitled users. When the
// tool is switched on the rest of the dieline dims and a ruler appears with two
// endpoint handles. The measurement is ALWAYS valid: both handles are real
// points on the dieline, they are always axis-aligned (the ruler is a straight
// horizontal or vertical line), and they are always on DIFFERENT edges — so the
// ruler measures ACROSS the shape and never runs along a single edge. Dragging
// a handle slides it along the edges; the far handle snaps to the real point
// straight across from it (where the horizontal/vertical line through the
// dragged handle meets another edge). The measure axis cannot run along the
// edge the dragged handle sits on: a point on a horizontal edge measures
// vertically, a point on a vertical edge measures horizontally, and on an
// angled edge the drag direction chooses. Nothing is saved — the
// "+ Add to drawing" persistence is a later phase.
//
// The real coordinates already live in the injected SVG: every dieline segment
// is a `<line class="svg-line" x1 y1 x2 y2>` in millimeters (1 SVG user unit =
// 1 mm). svg-pan-zoom wraps the geometry in a transformed group, so screen
// pixels are NOT millimeters at display time; we map between the two through the
// geometry's screen CTM.

const HANDLE_RADIUS_PX = 8;
const GRAB_RADIUS_PX = 26;
const CENTER_SNAP_PX = 16;
const AXIS_LOCK_THRESHOLD_PX = 6;
const COINCIDENT_MM = 0.5;
const PARALLEL_RATIO = 0.05;
const WITNESS_CAP_MM = 40;
const MM_PER_INCH = 25.4;

const svgRoot = () => document.querySelector('#svg');

// Only the real dieline segments carry a bare `svg-line`; the dimension-text
// and A/B/C helper lines are also `svg-line` but tagged `dmt-line`, so we
// exclude those everywhere (their endpoints are not real dieline points).
const GEOMETRY_LINE_SELECTOR = '#svg .svg-line:not(.dmt-line)';

const geometryMatrix = () => {
  const line = document.querySelector(GEOMETRY_LINE_SELECTOR);
  if (!line || typeof line.getScreenCTM !== 'function') { return null; }
  return line.getScreenCTM();
};

const userToClient = (point, matrix) => {
  const root = svgRoot();
  if (!root || !matrix || !point) { return null; }
  const svgPoint = root.createSVGPoint();
  svgPoint.x = point.x;
  svgPoint.y = point.y;
  return svgPoint.matrixTransform(matrix);
};

const clientToUser = (clientX, clientY, matrix) => {
  const root = svgRoot();
  if (!root || !matrix) { return null; }
  const svgPoint = root.createSVGPoint();
  svgPoint.x = clientX;
  svgPoint.y = clientY;
  return svgPoint.matrixTransform(matrix.inverse());
};

// The real dieline segments, in millimeters.
const gatherSegments = () => {
  const segments = [];
  document.querySelectorAll(GEOMETRY_LINE_SELECTOR).forEach((line) => {
    const ax = parseFloat(line.getAttribute('x1'));
    const ay = parseFloat(line.getAttribute('y1'));
    const bx = parseFloat(line.getAttribute('x2'));
    const by = parseFloat(line.getAttribute('y2'));
    if ([ax, ay, bx, by].some(Number.isNaN)) { return; }
    segments.push({ ax, ay, bx, by });
  });
  return segments;
};

// Whether two gathered segment sets describe the same dieline geometry — same
// count and identical endpoints in order. A redraw for the same dimensions (e.g.
// adding or removing an annotation) reproduces the geometry exactly, so this
// tells a geometry-changing redraw (new dimensions) apart from an annotation-only
// one.
const sameSegments = (a, b) => {
  if (a.length !== b.length) { return false; }
  return a.every((segment, index) => {
    const other = b[index];
    return segment.ax === other.ax && segment.ay === other.ay
      && segment.bx === other.bx && segment.by === other.by;
  });
};

// Circle and arc centres, in millimeters — snap targets for measuring between
// holes and notches. A circle carries its centre as `cx`/`cy`; an arc is a
// `<path>` with the centre emitted as `data-cx`/`data-cy` (y flipped like the
// rest of the geometry). Either centre is a real, formula-driven point, so a
// measurement snapped to it anchors and resizes exactly like an edge point.
const gatherCenters = () => {
  const centers = [];
  document.querySelectorAll('#svg .svg-circle').forEach((circle) => {
    const x = parseFloat(circle.getAttribute('cx'));
    const y = parseFloat(circle.getAttribute('cy'));
    if (![x, y].some(Number.isNaN)) { centers.push({ x, y }); }
  });
  document.querySelectorAll('#svg .svg-arc[data-cx]').forEach((arc) => {
    const x = parseFloat(arc.getAttribute('data-cx'));
    const y = parseFloat(arc.getAttribute('data-cy'));
    if (![x, y].some(Number.isNaN)) { centers.push({ x, y }); }
  });
  return centers;
};

// The circle/arc centre nearest the cursor within a screen-pixel snap radius, or null.
const nearestCenter = (centers, clientX, clientY, matrix) => {
  let best = null;
  let bestDistance = CENTER_SNAP_PX;
  centers.forEach((center) => {
    const client = userToClient(center, matrix);
    if (!client) { return; }
    const distance = Math.hypot(client.x - clientX, client.y - clientY);
    if (distance <= bestDistance) {
      bestDistance = distance;
      best = center;
    }
  });
  return best;
};

// Whether a segment runs (nearly) along the horizontal ('h') or vertical ('v')
// axis, or is angled (null). A measurement may not run along the axis its
// dragged handle's edge follows, and the far handle never lands on an edge that
// runs along the measurement axis — that is what keeps the two ends off the
// same line.
const segmentAxisParallel = (segment) => {
  const dx = Math.abs(segment.bx - segment.ax);
  const dy = Math.abs(segment.by - segment.ay);
  if (dy <= dx * PARALLEL_RATIO) { return 'h'; }
  if (dx <= dy * PARALLEL_RATIO) { return 'v'; }
  return null;
};

const segmentLength = (segment) => Math.hypot(segment.bx - segment.ax, segment.by - segment.ay);
const segmentMidpoint = (segment) => ({ x: (segment.ax + segment.bx) / 2, y: (segment.ay + segment.by) / 2 });

// The closest point to (px,py) lying on the segment, clamped to its ends.
const closestPointOnSegment = (px, py, ax, ay, bx, by) => {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  let t = lengthSquared === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / lengthSquared;
  t = Math.max(0, Math.min(1, t));
  return { x: ax + t * dx, y: ay + t * dy };
};

// The point on the dieline nearest the cursor (projection onto the closest
// edge), and which segment it lies on.
const nearestPointOnGeometry = (cursor, segments) => {
  let best = null;
  let bestIndex = -1;
  let bestDistance = Infinity;
  segments.forEach((segment, index) => {
    const point = closestPointOnSegment(cursor.x, cursor.y, segment.ax, segment.ay, segment.bx, segment.by);
    const distance = Math.hypot(point.x - cursor.x, point.y - cursor.y);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = point;
      bestIndex = index;
    }
  });
  return best ? { point: best, segmentIndex: bestIndex } : null;
};

// Where the horizontal/vertical line through `from` next meets an edge that
// does NOT run along that axis, nearest to `near`. Edges parallel to the
// measurement axis are skipped, so the far point is always on a different edge
// and the ruler crosses the interior rather than lying along a line. Returns
// null when no such crossing exists (e.g. at the extreme edge of the shape).
const alignedPointOnGeometry = (from, axis, segments, near) => {
  const candidates = [];
  const epsilon = 0.001;
  segments.forEach((segment) => {
    if (segmentAxisParallel(segment) === axis) { return; }
    if (axis === 'h') {
      const y = from.y;
      if (y < Math.min(segment.ay, segment.by) - epsilon || y > Math.max(segment.ay, segment.by) + epsilon) { return; }
      const t = (y - segment.ay) / (segment.by - segment.ay);
      candidates.push({ x: segment.ax + (segment.bx - segment.ax) * t, y });
    } else {
      const x = from.x;
      if (x < Math.min(segment.ax, segment.bx) - epsilon || x > Math.max(segment.ax, segment.bx) + epsilon) { return; }
      const t = (x - segment.ax) / (segment.bx - segment.ax);
      candidates.push({ x, y: segment.ay + (segment.by - segment.ay) * t });
    }
  });
  const distinct = candidates.filter((c) => Math.hypot(c.x - from.x, c.y - from.y) > COINCIDENT_MM);
  if (distinct.length === 0) { return null; }
  let best = distinct[0];
  let bestDistance = Infinity;
  distinct.forEach((candidate) => {
    const distance = Math.hypot(candidate.x - near.x, candidate.y - near.y);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  });
  return best;
};

// The axes a measurement may use from a handle on the given edge, most
// preferred first: never along the edge, and honouring the drag direction on an
// angled edge.
const allowedAxes = (draggedSegment, dragAxis) => {
  const parallel = draggedSegment ? segmentAxisParallel(draggedSegment) : null;
  if (parallel === 'h') { return ['v']; }
  if (parallel === 'v') { return ['h']; }
  if (dragAxis) { return [dragAxis, dragAxis === 'h' ? 'v' : 'h']; }
  return ['h', 'v'];
};

// Whether the ruler between a and b (running along `axis`) lies along an
// existing edge — i.e. it measures a section of a line, which is not a valid
// measurement. This catches the case where the two handles are on different
// edges but the straight line between them still coincides with an edge (e.g. a
// handle dragged to a corner). `a` and `b` share the off-axis coordinate.
const rulerOverlapsEdge = (a, b, axis, segments) => segments.some((segment) => {
  if (segmentAxisParallel(segment) !== axis) { return false; }
  if (axis === 'h') {
    if (Math.abs(segment.ay - a.y) > COINCIDENT_MM) { return false; }
    const overlap = Math.min(Math.max(a.x, b.x), Math.max(segment.ax, segment.bx))
      - Math.max(Math.min(a.x, b.x), Math.min(segment.ax, segment.bx));
    return overlap > COINCIDENT_MM;
  }
  if (Math.abs(segment.ax - a.x) > COINCIDENT_MM) { return false; }
  const overlap = Math.min(Math.max(a.y, b.y), Math.max(segment.ay, segment.by))
    - Math.max(Math.min(a.y, b.y), Math.min(segment.ay, segment.by));
  return overlap > COINCIDENT_MM;
});

const greatestCommonDivisor = (a, b) => (b === 0 ? a : greatestCommonDivisor(b, a % b));

const formatFractionalInch = (inches) => {
  const denominator = 16;
  const totalSixteenths = Math.round(inches * denominator);
  const whole = Math.floor(totalSixteenths / denominator);
  let numerator = totalSixteenths - whole * denominator;
  if (numerator === 0) { return `${whole}"`; }
  const divisor = greatestCommonDivisor(numerator, denominator);
  numerator /= divisor;
  const reducedDenominator = denominator / divisor;
  if (whole === 0) { return `${numerator}/${reducedDenominator}"`; }
  return `${whole} ${numerator}/${reducedDenominator}"`;
};

// A point on the segment from `from` toward `to`, no further than `cap`.
const cappedToward = (from, to, cap) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.hypot(dx, dy);
  if (distance <= cap || distance === 0) { return to; }
  const fraction = cap / distance;
  return { x: from.x + (dx * fraction), y: from.y + (dy * fraction) };
};

// Perpendicular distance from a point to a segment (screen px), for grabbing the
// dimension line.
const distanceToSegment = (px, py, ax, ay, bx, by) => {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = (dx * dx) + (dy * dy);
  let t = lengthSquared === 0 ? 0 : (((px - ax) * dx) + ((py - ay) * dy)) / lengthSquared;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + (t * dx)), py - (ay + (t * dy)));
};

// A floppy-disk "save" icon (Feather "save"), `size` px, centred at (centerX, 0)
// and drawn in the brand colour to sit on the white Save button.
const saveIcon = (centerX, size) => {
  const scale = size / 24;
  return (
    <g key="save-icon" className={styles.addCtaIcon}
      transform={`translate(${centerX - (size / 2)}, ${-(size / 2)}) scale(${scale})`}>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </g>
  );
};

// A live readout only — the saved annotation (later phase) is formatted
// server-side by DimensionText, which stays the authoritative value.
const formatMeasurement = (millimeters, unit) => {
  if (unit === 'mm') { return `${millimeters.toFixed(2)} mm`; }
  const inches = millimeters / MM_PER_INCH;
  if (unit === 'infr') { return formatFractionalInch(inches); }
  return `${inches.toFixed(3)}"`;
};

const MeasureTool = ({ svg, unit, theme, drawingId }) => {
  const [active, setActive] = useState(false);
  // The user's saved annotations on this drawing at the current dimensions, each
  // { id, mid_x, mid_y } — where its remove control is placed.
  const [annotations, setAnnotations] = useState([]);
  // Interaction lives in refs (it must not go stale inside the pointer
  // handlers); `tick` is bumped to re-render the overlay after a ref changes or
  // the view pans/zooms.
  const [, setTick] = useState(0);
  const rerender = () => setTick((value) => value + 1);

  const overlayRef = useRef(null);
  const segmentsRef = useRef([]);
  const centersRef = useRef([]);
  const handleARef = useRef(null);
  const handleBRef = useRef(null);
  const dragRef = useRef(null); // { name, axis, startX, startY } | null
  // How far the dimension line is moved off the measured points, perpendicular to
  // the axis (SVG space); 0 = through the points, dragging the line changes it.
  const offsetRef = useRef(0);
  // The id of the saved annotation being edited, or null when measuring a fresh
  // one — decides whether saving updates in place or creates.
  const editingIdRef = useRef(null);
  // Whether the tool was already active on the previous effect run, so an SVG
  // swap (a redraw) can be told apart from the tool being switched on.
  const wasActiveRef = useRef(false);

  // Seat a valid measurement across the drawing so the ruler is visible
  // immediately: a point on the longest vertical edge measured across to the
  // opposite side (or the longest horizontal edge measured vertically).
  // The centre of what's currently on screen, in the geometry's own space, or
  // null when the overlay/geometry matrix isn't ready yet.
  const viewportCenterPoint = () => {
    const overlay = overlayRef.current;
    const matrix = geometryMatrix();
    if (!overlay || !matrix) { return null; }
    const rect = overlay.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) { return null; }
    return clientToUser(rect.left + (rect.width / 2), rect.top + (rect.height / 2), matrix);
  };

  const placeDefaultRuler = () => {
    const segments = segmentsRef.current;
    if (segments.length < 1) {
      handleARef.current = null;
      handleBRef.current = null;
      return;
    }
    const longestParallelTo = (axis) => segments
      .filter((segment) => segmentAxisParallel(segment) === axis)
      .sort((a, b) => segmentLength(b) - segmentLength(a))[0];

    // Seat the ruler near the centre of what's currently visible so it appears on
    // screen even when the drawing is zoomed in; the across point aims at the same
    // spot so both ends stay near the viewport. Fall back to the longest edge's
    // midpoint (measuring the widest span) when the viewport can't be read.
    let from;
    let segment;
    let near = null;
    const viewportCenter = viewportCenterPoint();
    const hit = viewportCenter && nearestPointOnGeometry(viewportCenter, segments);
    if (hit) {
      from = hit.point;
      segment = segments[hit.segmentIndex];
      near = viewportCenter;
    } else {
      segment = longestParallelTo('v') || longestParallelTo('h')
        || [...segments].sort((a, b) => segmentLength(b) - segmentLength(a))[0];
      from = segmentMidpoint(segment);
    }
    const parallel = segmentAxisParallel(segment);
    const axis = parallel === 'h' ? 'v' : 'h';

    const acrossFor = (candidateAxis) => {
      const target = near || (candidateAxis === 'h'
        ? { x: Number.MAX_SAFE_INTEGER, y: from.y }
        : { x: from.x, y: -Number.MAX_SAFE_INTEGER });
      const point = alignedPointOnGeometry(from, candidateAxis, segments, target);
      return (point && !rulerOverlapsEdge(from, point, candidateAxis, segments)) ? point : null;
    };
    const across = acrossFor(axis) || acrossFor(axis === 'h' ? 'v' : 'h');
    if (across) {
      handleARef.current = from;
      handleBRef.current = across;
    } else {
      handleARef.current = null;
      handleBRef.current = null;
    }
  };

  // The user's saved annotations, positioned at the current dimensions so each
  // remove control lands on its annotation. Options must match the render the
  // annotations were injected into, so pass the current drawing options.
  const fetchAnnotations = () => {
    if (!drawingId) { return; }
    const query = new URLSearchParams();
    // Serialise the FULL options — arrays and nested objects included, in Rails
    // bracket notation — so the list redraws the SAME geometry the SVG/PDF render
    // injects into. Dropping non-scalar options (e.g. the `add_ons` array) made
    // the list redraw a different drawing, so annotations on that geometry got no
    // control and every control's position was computed from the wrong geometry.
    const appendOption = (name, value) => {
      if (value === null || value === undefined) { return; }
      if (Array.isArray(value)) {
        value.forEach((item) => appendOption(`${name}[]`, item));
      } else if (typeof value === 'object') {
        Object.entries(value).forEach(([key, nested]) => appendOption(`${name}[${key}]`, nested));
      } else {
        query.append(name, value);
      }
    };
    Object.entries(window.currentDrawingOptions || {}).forEach(([key, value]) => appendOption(`options[${key}]`, value));
    fetch(`/measure-tool/${drawingId}/annotations?${query.toString()}`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : { annotations: [] }))
      // Exclude the annotation currently being edited: it IS the live ruler, so its
      // own edit/remove controls stay hidden (loadAnnotation removed it) until
      // editing ends — otherwise a redraw's re-fetch would re-add them on top of it.
      .then((data) => setAnnotations((data.annotations || []).filter((annotation) => annotation.id !== editingIdRef.current)))
      .catch(() => { /* keep whatever list we have */ });
  };

  // Remove one of the user's own annotations, then redraw so the server drops it
  // from the render. Clear it optimistically so its control disappears at once.
  const removeAnnotation = (annotationId) => {
    if (!drawingId) { return; }
    const token = document.querySelector('meta[name=csrf-token]')?.getAttribute('content');
    fetch(`/measure-tool/${drawingId}/annotations/${annotationId}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': token },
    }).then((response) => {
      if (response.ok) {
        trackEvent('measure_annotation_removed', {});
        setAnnotations((current) => current.filter((annotation) => annotation.id !== annotationId));
        document.dispatchEvent(new CustomEvent('measure_tool.annotation_changed'));
      }
    });
  };

  // Re-open a saved annotation as the live ruler so it can be repositioned or
  // re-measured; its id is remembered so saving updates it in place. The server
  // sends drawing-space coordinates, so the SVG's flipped y is restored here.
  const loadAnnotation = (annotation) => {
    // Both ends are deliberate (they were saved), so keep them pinned on edit.
    const a = { x: annotation.ax, y: -annotation.ay, placed: true };
    const b = { x: annotation.bx, y: -annotation.by, placed: true };
    handleARef.current = a;
    handleBRef.current = b;
    // Restore the offset in the client's convention (y-sign flipped for a horizontal).
    const stored = annotation.offset || 0;
    const horizontal = Math.abs(a.x - b.x) >= Math.abs(a.y - b.y);
    offsetRef.current = horizontal ? -stored : stored;
    editingIdRef.current = annotation.id;
    dragRef.current = null;
    // Hide its controls while it is the live ruler.
    setAnnotations((current) => current.filter((item) => item.id !== annotation.id));
    rerender();
  };

  // Re-gather the geometry whenever it changes (a redraw replaces the SVG) or
  // the tool is switched on. Re-seat the default ruler when the tool is switched
  // ON, or when the DIELINE GEOMETRY actually changed (a dimensions redraw). An
  // annotation add/remove redraws the SAME geometry, so the in-progress ruler is
  // kept — removing one annotation must not discard the measurement in progress.
  useEffect(() => {
    if (!active) {
      wasActiveRef.current = false;
      return;
    }
    const justActivated = !wasActiveRef.current;
    wasActiveRef.current = true;
    const previousSegments = segmentsRef.current;
    segmentsRef.current = gatherSegments();
    centersRef.current = gatherCenters();
    fetchAnnotations();
    if (justActivated || !sameSegments(previousSegments, segmentsRef.current)) {
      placeDefaultRuler();
      dragRef.current = null;
      offsetRef.current = 0;
      editingIdRef.current = null;
    }
    rerender();
  }, [active, svg]);

  // While the tool is active, dim the rest of the dieline so the ruler stands
  // out. The class is re-applied on `svg` change (a redraw injects a new #svg).
  useEffect(() => {
    const target = svgRoot();
    if (!active || !target) { return undefined; }
    target.classList.add(styles.dimmed);
    return () => { target.classList.remove(styles.dimmed); };
  }, [active, svg]);

  // Keep the overlay aligned with the geometry as the user pans/zooms/scrolls.
  useEffect(() => {
    if (!active) { return undefined; }

    const root = svgRoot();
    let observer = null;
    if (root && typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(rerender);
      observer.observe(root, { attributes: true, attributeFilter: ['transform'], subtree: true });
    }
    window.addEventListener('resize', rerender);
    window.addEventListener('scroll', rerender, true);

    const onKeyDown = (event) => {
      if (event.key === 'Escape') { placeDefaultRuler(); offsetRef.current = 0; rerender(); }
    };
    window.addEventListener('keydown', onKeyDown);

    // React's onWheel is passive, so preventDefault there is ignored. Attach a
    // native non-passive listener so mouse-wheel zoom can be forwarded to
    // pan-zoom (and the page kept from scrolling) while the overlay is active.
    const overlay = overlayRef.current;
    const onWheel = (event) => {
      const instance = window.svgPanZoomInstance;
      const target = svgRoot();
      if (!instance || !target) { return; }
      event.preventDefault();
      const rect = target.getBoundingClientRect();
      // Zoom proportional to the scroll amount, normalised across wheel/trackpad
      // and clamped. A fixed step per event compounded wildly on a trackpad, which
      // fires many events per gesture — the cause of the runaway zoom.
      let delta = event.deltaY;
      if (event.deltaMode === 1) { delta *= 16; }
      else if (event.deltaMode === 2) { delta *= rect.height; }
      delta = Math.max(-100, Math.min(100, delta));
      const factor = Math.exp(-delta * 0.0015);
      instance.zoomAtPoint(instance.getZoom() * factor, {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
      rerender();
    };
    if (overlay) { overlay.addEventListener('wheel', onWheel, { passive: false }); }

    return () => {
      if (observer) { observer.disconnect(); }
      window.removeEventListener('resize', rerender);
      window.removeEventListener('scroll', rerender, true);
      window.removeEventListener('keydown', onKeyDown);
      if (overlay) { overlay.removeEventListener('wheel', onWheel); }
    };
  }, [active, svg]);

  const handleUnderCursor = (clientX, clientY, matrix) => {
    const candidates = [['A', handleARef.current], ['B', handleBRef.current]];
    let grabbed = null;
    let bestDistance = GRAB_RADIUS_PX;
    candidates.forEach(([name, point]) => {
      const client = userToClient(point, matrix);
      if (!client) { return; }
      const distance = Math.hypot(client.x - clientX, client.y - clientY);
      if (distance <= bestDistance) {
        bestDistance = distance;
        grabbed = name;
      }
    });
    return grabbed;
  };

  const isHorizontalRuler = () => {
    const a = handleARef.current;
    const b = handleBRef.current;
    return !a || !b || Math.abs(a.x - b.x) >= Math.abs(a.y - b.y);
  };

  // Endpoints of the dimension line in SVG user space, or null. The line is ALWAYS
  // perfectly horizontal or vertical. It sits at the FIRST handle's level (plus the
  // drag offset), so when the two points are not aligned the second projects to the
  // intersection corner — an L with a witness leg to that point — rather than
  // floating at their midpoint. For aligned points both share the level, so it is
  // unchanged. Shared by the render and the grab-detection so they always agree.
  const dimensionLineUserEnds = () => {
    const a = handleARef.current;
    const b = handleBRef.current;
    if (!a || !b) { return null; }
    const horizontal = isHorizontalRuler();
    const lineCoord = (horizontal ? a.y : a.x) + offsetRef.current;
    return {
      start: horizontal ? { x: a.x, y: lineCoord } : { x: lineCoord, y: a.y },
      end: horizontal ? { x: b.x, y: lineCoord } : { x: lineCoord, y: b.y },
    };
  };

  // The same endpoints in client space, for grabbing the dimension line.
  const dimensionLineEnds = (matrix) => {
    const ends = dimensionLineUserEnds();
    if (!ends) { return null; }
    const start = userToClient(ends.start, matrix);
    const end = userToClient(ends.end, matrix);
    return (start && end) ? { start, end } : null;
  };

  // Start dragging the dimension line perpendicular (out of the drawing). Shared
  // by the ruler line's grab zone and the value label, so the whole label is a
  // move target — not only the sliver that happens to sit within the thin line's
  // grab radius (a vertical measurement's label sits off to the side).
  const beginOffsetDrag = (event) => {
    try { overlayRef.current?.setPointerCapture(event.pointerId); } catch (error) { /* no-op */ }
    const cursor = clientToUser(event.clientX, event.clientY, geometryMatrix());
    if (!cursor) { return; }
    dragRef.current = {
      name: 'offset', horizontal: isHorizontalRuler(),
      startOffset: offsetRef.current, startUserX: cursor.x, startUserY: cursor.y,
    };
    rerender();
  };

  const onPointerDown = (event) => {
    const matrix = geometryMatrix();
    // Pointer capture keeps the drag alive if the cursor leaves the overlay; it
    // is only a nicety, so ignore environments where it is unavailable.
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch (error) { /* no-op */ }

    // Grabbing a handle changes the measurement.
    const grabbed = handleUnderCursor(event.clientX, event.clientY, matrix);
    if (grabbed) {
      // Snapshot both ends so an invalid drop can restore the last valid line.
      dragRef.current = {
        name: grabbed, axis: null, startX: event.clientX, startY: event.clientY,
        prevA: handleARef.current, prevB: handleBRef.current,
      };
      rerender();
      return;
    }

    // Grabbing the dimension line moves it perpendicular (out of the drawing).
    const ends = dimensionLineEnds(matrix);
    const onLine = ends && distanceToSegment(event.clientX, event.clientY, ends.start.x, ends.start.y, ends.end.x, ends.end.y) <= GRAB_RADIUS_PX;
    if (onLine) {
      beginOffsetDrag(event);
      return;
    }

    // Otherwise pan the drawing (the overlay swallows pointer events, so panning
    // is forwarded to pan-zoom).
    dragRef.current = { name: 'pan', lastX: event.clientX, lastY: event.clientY };
    rerender();
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) { return; }
    if (drag.name === 'pan') {
      const instance = window.svgPanZoomInstance;
      if (instance && typeof instance.panBy === 'function') {
        instance.panBy({ x: event.clientX - drag.lastX, y: event.clientY - drag.lastY });
        drag.lastX = event.clientX;
        drag.lastY = event.clientY;
      }
      rerender();
      return;
    }
    if (drag.name === 'offset') {
      const cursor = clientToUser(event.clientX, event.clientY, geometryMatrix());
      if (!cursor) { return; }
      offsetRef.current = drag.startOffset + (drag.horizontal ? cursor.y - drag.startUserY : cursor.x - drag.startUserX);
      rerender();
      return;
    }
    const matrix = geometryMatrix();
    const cursor = clientToUser(event.clientX, event.clientY, matrix);
    if (!cursor) { return; }

    // Circle/arc-centre snapping. If the dragged handle is over a centre, snap it
    // there; if the OTHER handle is already on a centre, this handle follows the
    // nearest edge point. Every measurement is squared to an axis-aligned line on
    // release (squareUpAcross) — two centres and centre-to-edge alike, never a
    // diagonal — like an edge-to-edge measurement.
    const draggedRef = drag.name === 'A' ? handleARef : handleBRef;
    const otherRef = drag.name === 'A' ? handleBRef : handleARef;
    const centerHit = nearestCenter(centersRef.current, event.clientX, event.clientY, matrix);
    if (centerHit) {
      draggedRef.current = { x: centerHit.x, y: centerHit.y, center: centerHit, placed: true };
      rerender();
      return;
    }
    if (otherRef.current && otherRef.current.center) {
      const nearest = nearestPointOnGeometry(cursor, segmentsRef.current);
      draggedRef.current = nearest ? { x: nearest.point.x, y: nearest.point.y, placed: true } : { x: cursor.x, y: cursor.y, placed: true };
      rerender();
      return;
    }

    // Move only the dragged handle, snapped to the nearest real point; the other
    // handle stays put. The measurement is squared up to a valid axis-aligned line
    // on release (onPointerUp), not on every move — so the two ends can be placed
    // one at a time without the far one flying around.
    const hit = nearestPointOnGeometry(cursor, segmentsRef.current);
    if (!hit) { rerender(); return; }
    draggedRef.current = { x: hit.point.x, y: hit.point.y, placed: true };
    rerender();
  };

  // Pull the edge handle onto the horizontal/vertical line through the fixed
  // centre handle (the axis closest to the current orientation), so a
  // centre-to-edge measurement is axis-aligned like an edge-to-edge one. Restores
  // the last valid line if the centre has no aligned edge on that side.
  const squareEdgeToCenter = (centerRef, edgeRef, drag) => {
    const center = centerRef.current;
    const edge = edgeRef.current;
    const axes = Math.abs(edge.x - center.x) >= Math.abs(edge.y - center.y) ? ['h', 'v'] : ['v', 'h'];
    let aligned = null;
    axes.some((axis) => {
      const candidate = alignedPointOnGeometry(center, axis, segmentsRef.current, edge);
      if (candidate) { aligned = candidate; return true; }
      return false;
    });
    if (aligned) {
      edgeRef.current = { x: aligned.x, y: aligned.y };
    } else {
      handleARef.current = drag.prevA;
      handleBRef.current = drag.prevB;
    }
  };

  // On release, seat the handles so the measurement is a clean axis-aligned line:
  // move the far handle straight across from the one just placed, or, when one end
  // is a snapped centre, pull the edge end onto the line through it. A handle the
  // user deliberately placed (dragged onto its own point) is never relocated — so
  // you can pin BOTH ends on specific points; the render then projects the far one
  // to the intersection corner. Likewise when the far handle can't be aligned (two
  // centres, or no edge straight across — a gap on that side), both points stay put
  // and project. The span is always horizontal or vertical, never a diagonal.
  const squareUpAcross = (drag) => {
    const draggedRef = drag.name === 'A' ? handleARef : handleBRef;
    const otherRef = drag.name === 'A' ? handleBRef : handleARef;
    const dragged = draggedRef.current;
    const other = otherRef.current;
    if (!dragged || !other) { return; }
    if (dragged.center && other.center) { return; }
    if (dragged.center || other.center) {
      const edgeRef = dragged.center ? otherRef : draggedRef;
      // Keep an edge end the user placed themselves; otherwise align it to the centre.
      if (!edgeRef.current.placed) {
        squareEdgeToCenter(dragged.center ? draggedRef : otherRef, edgeRef, drag);
      }
      return;
    }

    // The other end was deliberately placed: keep both and project (don't seat it
    // across, which would drag it off the point the user chose) — UNLESS the two
    // points make the span lie along an existing edge, which is never a valid
    // measurement (it measures a section of one line). Then restore the last valid
    // line, like the other invalid drops do. Centre pairs are handled above, so
    // this only ever guards two placed EDGE points.
    if (other.placed) {
      const axis = isHorizontalRuler() ? 'h' : 'v';
      if (rulerOverlapsEdge(handleARef.current, handleBRef.current, axis, segmentsRef.current)) {
        handleARef.current = drag.prevA;
        handleBRef.current = drag.prevB;
      }
      return;
    }

    const hit = nearestPointOnGeometry(dragged, segmentsRef.current);
    const draggedSegment = hit ? segmentsRef.current[hit.segmentIndex] : null;
    let across = null;
    allowedAxes(draggedSegment, null).some((axis) => {
      const candidate = alignedPointOnGeometry(dragged, axis, segmentsRef.current, otherRef.current);
      if (candidate && !rulerOverlapsEdge(dragged, candidate, axis, segmentsRef.current)) {
        across = candidate;
        return true;
      }
      return false;
    });
    if (across) {
      otherRef.current = across;
    }
    // Otherwise keep both real points where they are; the projected-corner render
    // makes the non-aligned span a clean axis-aligned line.
  };

  const onPointerUp = () => {
    const drag = dragRef.current;
    if (!drag) { return; }
    dragRef.current = null;
    if (drag.name === 'A' || drag.name === 'B') { squareUpAcross(drag); }
    rerender();
  };

  // Persist the current ruler as one of the user's own annotations, then ask the
  // drawing to redraw so the server injects it into the render. The handles are
  // in SVG user space (y flipped); the server wants the drawing's own y.
  const addToDrawing = () => {
    const a = handleARef.current;
    const b = handleBRef.current;
    if (!a || !b || !drawingId) { return; }
    // Every measurement is an axis-aligned dimension. Convert the SVG-space offset
    // to the drawing's own y sign, and send null when the line was not moved so the
    // server applies its default placement.
    const horizontal = Math.abs(a.x - b.x) >= Math.abs(a.y - b.y);
    const offset = offsetRef.current;
    const token = document.querySelector('meta[name=csrf-token]')?.getAttribute('content');
    // Editing a saved annotation updates it in place; otherwise create a new one.
    const editingId = editingIdRef.current;
    const url = editingId
      ? `/measure-tool/${drawingId}/annotations/${editingId}`
      : `/measure-tool/${drawingId}/annotations`;
    fetch(url, {
      method: editingId ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
      body: JSON.stringify({
        point_a: { x: a.x, y: -a.y },
        point_b: { x: b.x, y: -b.y },
        offset: Math.abs(offset) < 0.01 ? null : (horizontal ? -offset : offset),
        options: window.currentDrawingOptions || {},
      }),
    }).then((response) => {
      if (response.ok) {
        trackEvent('measure_annotation_saved', {});
        document.dispatchEvent(new CustomEvent('measure_tool.annotation_changed'));
        // Adding is a commit: close the tool, as clicking Done would.
        setActive(false);
      }
    });
  };

  // Markers at every circle/arc centre so the snap targets are visible while
  // measuring — the user can drag a handle straight onto one instead of guessing
  // where the centre sits. Purely visual (no pointer events), positioned like the
  // handles through the geometry's screen CTM.
  const draggingHandle = () => dragRef.current?.name === 'A' || dragRef.current?.name === 'B';

  const renderCenterMarkers = () => {
    const overlay = overlayRef.current;
    const matrix = geometryMatrix();
    if (!overlay || !matrix || centersRef.current.length === 0) { return null; }
    const overlayRect = overlay.getBoundingClientRect();
    // Grow/brighten the centres while a handle is being dragged, so they are easy
    // to aim for.
    const dragging = draggingHandle();
    return centersRef.current.map((center, index) => {
      const client = userToClient(center, matrix);
      if (!client) { return null; }
      return (
        <circle
          key={`center-${index}`}
          className={dragging ? styles.centerMarkerActive : styles.centerMarker}
          cx={client.x - overlayRect.left}
          cy={client.y - overlayRect.top}
          r={dragging ? 6 : 4}
        />
      );
    });
  };

  // A small remove control on each saved annotation. The server gives the
  // midpoint in the drawing's own space; the SVG's y is flipped from it (the same
  // flip the save uses), so the control is placed at { mid_x, -mid_y }.
  const renderAnnotationRemovers = () => {
    const overlay = overlayRef.current;
    const matrix = geometryMatrix();
    if (!overlay || !matrix || annotations.length === 0) { return null; }
    const overlayRect = overlay.getBoundingClientRect();
    return annotations.map((annotation) => {
      const client = userToClient({ x: annotation.mid_x, y: -annotation.mid_y }, matrix);
      if (!client) { return null; }
      const cx = client.x - overlayRect.left;
      const cy = client.y - overlayRect.top;
      return (
        <g key={`controls-${annotation.id}`}>
          <g className={styles.remover} transform={`translate(${cx - 13}, ${cy})`}
            onPointerDown={(event) => { event.stopPropagation(); loadAnnotation(annotation); }}>
            <circle className={styles.editBackground} r="11" />
            <g className={styles.removerMark} transform="translate(-7, -7) scale(0.58)">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" fill="none" />
            </g>
          </g>
          <g className={styles.remover} transform={`translate(${cx + 13}, ${cy})`}
            onPointerDown={(event) => { event.stopPropagation(); removeAnnotation(annotation.id); }}>
            <circle className={styles.removerBackground} r="11" />
            <line className={styles.removerMark} x1="-4" y1="-4" x2="4" y2="4" />
            <line className={styles.removerMark} x1="4" y1="-4" x2="-4" y2="4" />
          </g>
        </g>
      );
    });
  };

  // The value pill and the contextual Save button (a save icon + label), centred
  // at `center` (local overlay coords). When `draggable`, the whole pill is a move
  // target for repositioning the dimension line off the geometry.
  const labelAndCta = (center, valueText, draggable) => {
    const labelWidth = (valueText.length * 8) + 14;
    const labelHeight = 20;
    const saveText = t('measure_tool.add');
    const iconSize = 14;
    const iconGap = 6;
    const addWidth = 12 + iconSize + iconGap + (saveText.length * 8) + 12;
    const addHeight = 24;
    const addCenter = { x: center.x + (labelWidth / 2) + 8 + (addWidth / 2), y: center.y };
    const iconX = (-addWidth / 2) + 12 + (iconSize / 2);
    const textX = (-addWidth / 2) + 12 + iconSize + iconGap;
    const labelProps = draggable
      ? { style: { cursor: 'move' }, onPointerDown: (event) => { event.stopPropagation(); beginOffsetDrag(event); } }
      : {};
    return [
      <g key="label" transform={`translate(${center.x}, ${center.y})`} {...labelProps}>
        <rect className={styles.labelBackground}
          x={-labelWidth / 2} y={-labelHeight / 2} width={labelWidth} height={labelHeight} rx="4" />
        <text className={styles.labelText} x="0" y="0" dominantBaseline="central" textAnchor="middle">{valueText}</text>
      </g>,
      <g key="add-cta" className={styles.addCta}
        transform={`translate(${addCenter.x}, ${addCenter.y})`}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => { event.stopPropagation(); addToDrawing(); }}>
        <rect className={styles.addCtaBackground}
          x={-addWidth / 2} y={-addHeight / 2} width={addWidth} height={addHeight} rx="5" />
        {saveIcon(iconX, iconSize)}
        <text className={styles.addCtaText} x={textX} y="0" dominantBaseline="central" textAnchor="start">{saveText}</text>
      </g>,
    ];
  };

  const renderOverlayContents = () => {
    const overlay = overlayRef.current;
    const matrix = geometryMatrix();
    const a = handleARef.current;
    const b = handleBRef.current;
    if (!overlay || !matrix || !a || !b) { return null; }

    const overlayRect = overlay.getBoundingClientRect();
    const toLocal = (userPoint) => {
      const client = userToClient(userPoint, matrix);
      if (!client) { return null; }
      return { x: client.x - overlayRect.left, y: client.y - overlayRect.top };
    };

    const aLocal = toLocal(a);
    const bLocal = toLocal(b);
    if (!aLocal || !bLocal) { return null; }

    // The dimension line is ALWAYS perfectly horizontal or vertical, whatever the
    // two measured points are — edges (squared up to the same level) or two
    // circle/arc centres at different levels. It sits on one axis (see
    // dimensionLineUserEnds); witness lines connect it back to each measured point
    // when the point is off the line.
    const horizontal = Math.abs(a.x - b.x) >= Math.abs(a.y - b.y);
    const millimeters = horizontal ? Math.abs(a.x - b.x) : Math.abs(a.y - b.y);
    const ends = dimensionLineUserEnds();
    const mainStart = toLocal(ends.start);
    const mainEnd = toLocal(ends.end);
    if (!mainStart || !mainEnd) { return null; }

    const label = formatMeasurement(millimeters, unit);
    const labelCenter = horizontal
      ? { x: (mainStart.x + mainEnd.x) / 2, y: (mainStart.y + mainEnd.y) / 2 - 14 }
      : { x: (mainStart.x + mainEnd.x) / 2 + 18, y: (mainStart.y + mainEnd.y) / 2 };

    const elements = [
      <line key="ruler" className={styles.rulerLine} markerStart="url(#measureArrow)" markerEnd="url(#measureArrow)"
        style={{ cursor: 'move' }}
        x1={mainStart.x} y1={mainStart.y} x2={mainEnd.x} y2={mainEnd.y} />,
    ];

    // A witness line from each dimension-line end back to its measured point,
    // whenever that point is off the line (an offset, or a centre off the axis).
    const addWitness = (end, endUser, point, key) => {
      if (Math.hypot(endUser.x - point.x, endUser.y - point.y) <= 0.01) { return; }
      const witness = toLocal(cappedToward(endUser, point, WITNESS_CAP_MM));
      if (witness) { elements.push(<line key={key} className={styles.witness} x1={end.x} y1={end.y} x2={witness.x} y2={witness.y} />); }
    };
    addWitness(mainStart, ends.start, a, 'witness-A');
    addWitness(mainEnd, ends.end, b, 'witness-B');

    elements.push(
      <circle key="handle-A" className={`${styles.handle} ${dragRef.current?.name === 'A' ? styles.handleActive : ''}`}
        cx={aLocal.x} cy={aLocal.y} r={HANDLE_RADIUS_PX} />,
      <circle key="handle-B" className={`${styles.handle} ${dragRef.current?.name === 'B' ? styles.handleActive : ''}`}
        cx={bLocal.x} cy={bLocal.y} r={HANDLE_RADIUS_PX} />,
      ...labelAndCta(labelCenter, label, true),
    );
    return elements;
  };

  const themeClass = theme === 'black_bg' ? styles.dark : styles.light;

  // Reflect the active drag on the whole overlay so the cursor stays right even
  // as the pointer leaves the line: moving the dimension line off the drawing, or
  // panning the canvas.
  let overlayCursor;
  if (dragRef.current?.name === 'pan') { overlayCursor = 'grabbing'; }
  else if (dragRef.current?.name === 'offset') { overlayCursor = 'move'; }

  return (
    <>
      <div className={`${styles.toolbar} ${themeClass}`}>
        {!active &&
          <button
            type="button"
            className={`dct-button dct-button--small dct-button--secondary ${styles.toggle}`}
            onClick={() => { trackEvent('measure_tool_opened', {}); setActive(true); }}
          >
            <svg className={styles.buttonIcon} width="22" height="22" viewBox="0 0 16 16" aria-hidden="true">
              <rect x="0.75" y="3.5" width="14.5" height="9" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
              <line x1="4" y1="3.5" x2="4" y2="8.5" stroke="currentColor" strokeWidth="1.3" />
              <line x1="8" y1="3.5" x2="8" y2="7" stroke="currentColor" strokeWidth="1.3" />
              <line x1="12" y1="3.5" x2="12" y2="8.5" stroke="currentColor" strokeWidth="1.3" />
            </svg>
            {t('measure_tool.button')}
          </button>}
        {active &&
          <button
            type="button"
            className={`dct-button dct-button--small dct-button--secondary ${styles.toggle}`}
            onClick={() => setActive(false)}
          >
            {t('measure_tool.done')}
          </button>}
        {active && <span className={styles.hint}>{t('measure_tool.hint')}</span>}
      </div>

      {active &&
        <svg
          ref={overlayRef}
          className={styles.overlay}
          style={{ cursor: overlayCursor }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          <defs>
            <marker id="measureArrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 1 L 9 5 L 0 9 z" className={styles.arrowHead} />
            </marker>
          </defs>
          <rect className={styles.capture} x="0" y="0" width="100%" height="100%" pointerEvents="all" />
          {renderCenterMarkers()}
          {renderOverlayContents()}
          {renderAnnotationRemovers()}
        </svg>}
    </>
  );
};

export default MeasureTool;
